import { useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { useToast } from '../../../contexts/ToastContext';
import { utcToDisplayDateAndTime, displayDateTimeToUTC } from '../../../utils/timezoneUtils';
import { WO_DETAIL_TABS } from '../utils/maintenanceUtils';

/**
 * Custom hook for maintenance handlers (CRUD operations and workflow)
 */
export const useMaintenanceHandlers = (fetchMaintenance, displayTimezone = 'Asia/Kolkata') => {
  const [open, setOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedMaintenance, setSelectedMaintenance] = useState(null);
  const [detailTab, setDetailTab] = useState(WO_DETAIL_TABS.OVERVIEW);
  
  // Workflow dialog states
  const [camoQmAckOpen, setCamoQmAckOpen] = useState(false);
  const [planningOpen, setPlanningOpen] = useState(false);
  const [storeOpen, setStoreOpen] = useState(false);
  const [externalAgencyOpen, setExternalAgencyOpen] = useState(false);
  const [mroOpen, setMroOpen] = useState(false);
  const [mroEngineerOpen, setMroEngineerOpen] = useState(false);
  const [mroQmCompletionOpen, setMroQmCompletionOpen] = useState(false);
  const [camoQmCompletionOpen, setCamoQmCompletionOpen] = useState(false);
  const [partsProcurementOpen, setPartsProcurementOpen] = useState(false);
  const [releaseCertificateOpen, setReleaseCertificateOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [amOverrideJustification, setAmOverrideJustification] = useState('');

  const [formData, setFormData] = useState({
    aircraftId: '',
    type: 'INSPECTION',
    title: '',
    description: '',
    priority: 'MEDIUM',
    status: 'SCHEDULED',
    scheduledDate: null,
    scheduleMode: 'FULL_DAY',
    scheduledEndDate: null,
    inspectionType: '',
    ampScheduleId: '',
    ampChecks: [],
    creationSourceRemarks: '',
    linkedIntimationIds: [],
    workOrderCsvFile: null,
    standaloneEmergencyRepair: false
  });

  const { showToast } = useToast();

  const handleSubmit = async () => {
    try {
      if (!selectedMaintenance) {
        if (!formData.aircraftId || !formData.aircraftId.toString().trim()) {
          showToast('Please select an aircraft.', 'warning', 5000);
          return;
        }
        if (!formData.title || !String(formData.title).trim()) {
          showToast('Please enter a title for the work order.', 'warning', 5000);
          return;
        }
        const hasLinkedIntimations = Array.isArray(formData.linkedIntimationIds) && formData.linkedIntimationIds.length > 0;
        if (!hasLinkedIntimations && !(String(formData.creationSourceRemarks || '').trim())) {
          showToast('Creation source / reason is required when the work order is not linked to any intimation.', 'warning', 5000);
          return;
        }
        if (!formData.standaloneEmergencyRepair) {
          const checklist = Array.isArray(formData.ampChecks)
            ? formData.ampChecks.filter((c) => String(c?.description || '').trim())
            : [];
          if (checklist.length === 0) {
            showToast('Enter at least one checklist item manually before raising this work order.', 'warning', 5000);
            return;
          }
        }
      }
      const payload = { ...formData };
      if (selectedMaintenance) {
        delete payload.standaloneEmergencyRepair;
      }
      if (amOverrideJustification) {
        payload.overrideJustification = amOverrideJustification;
      }
      if (!payload.inspectionType) {
        delete payload.inspectionType;
      }
      if (!payload.ampScheduleId) {
        delete payload.ampScheduleId;
        delete payload.maintenanceType;
        delete payload.assemblies;
      }
      if (selectedMaintenance) {
        delete payload.ampChecks;
      } else {
        const checklist = Array.isArray(payload.ampChecks)
          ? payload.ampChecks.filter((c) => String(c?.description || '').trim())
          : [];
        if (checklist.length > 0) {
          payload.ampChecks = checklist;
        } else {
          delete payload.ampChecks;
        }
      }
      if (!payload.standaloneEmergencyRepair) {
        delete payload.standaloneEmergencyRepair;
      }
      // Serialize schedule window (display TZ -> UTC when displayTimezone set)
      const mode = payload.scheduleMode === 'HOURLY' ? 'HOURLY' : 'FULL_DAY';
      payload.scheduleMode = mode;
      if (payload.scheduledDate != null) {
        const d = dayjs(payload.scheduledDate);
        if (d.isValid()) {
          if (mode === 'HOURLY') {
            payload.scheduledDate = displayTimezone
              ? (displayDateTimeToUTC(d.format('YYYY-MM-DD'), d.format('HH:mm'), displayTimezone) ?? d.toISOString())
              : d.toISOString();
            const end = payload.scheduledEndDate != null ? dayjs(payload.scheduledEndDate) : null;
            if (!end || !end.isValid()) {
              showToast('End time is required for an hourly schedule', 'warning', 5000);
              return;
            }
            if (!end.isAfter(d)) {
              showToast('Schedule end must be after schedule start', 'warning', 5000);
              return;
            }
            payload.scheduledEndDate = displayTimezone
              ? (displayDateTimeToUTC(end.format('YYYY-MM-DD'), end.format('HH:mm'), displayTimezone) ?? end.toISOString())
              : end.toISOString();
          } else {
            payload.scheduledDate = displayTimezone
              ? (displayDateTimeToUTC(d.format('YYYY-MM-DD'), '00:00', displayTimezone) ?? d.toISOString())
              : d.toISOString();
            payload.scheduledEndDate = null;
          }
        } else {
          payload.scheduledDate = null;
          payload.scheduledEndDate = null;
        }
      } else {
        payload.scheduledEndDate = null;
      }
      // Ensure linkedIntimationIds is an array of IDs for create/update
      if (Array.isArray(payload.linkedIntimationIds)) {
        payload.linkedIntimationIds = payload.linkedIntimationIds.map((i) => (i && (i._id || i)).toString());
      }

      const submitAsMultipart = Boolean(payload.workOrderCsvFile);
      if (submitAsMultipart) {
        const multipartPayload = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value === undefined || value === null || key === 'workOrderCsvFile') return;
          if (Array.isArray(value)) {
            multipartPayload.append(key, JSON.stringify(value));
            return;
          }
          multipartPayload.append(key, value);
        });
        multipartPayload.append('workOrderCsvFile', payload.workOrderCsvFile);
        if (selectedMaintenance) {
          await axios.put(`/api/maintenance/${selectedMaintenance._id}`, multipartPayload);
        } else {
          await axios.post('/api/maintenance', multipartPayload);
        }
      } else if (selectedMaintenance) {
        await axios.put(`/api/maintenance/${selectedMaintenance._id}`, payload);
      } else {
        await axios.post('/api/maintenance', payload);
      }
      setAmOverrideJustification('');
      fetchMaintenance();
      handleClose();
    } catch (error) {
      console.error('Error saving maintenance:', error);
      const data = error.response?.data;
      let message = data?.message;
      if (!message && Array.isArray(data?.errors) && data.errors.length > 0) {
        const first = data.errors[0];
        const field = first.path || first.param || 'field';
        const msg = first.msg || first.message || 'Invalid value';
        const fieldLabel = { aircraftId: 'Aircraft', title: 'Title', type: 'Type' }[field] || field;
        message = `${fieldLabel}: ${msg}`;
      }
      if (!message && data?.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
        const firstKey = Object.keys(data.errors)[0];
        const firstErr = data.errors[firstKey];
        const msg = firstErr?.message || firstErr?.properties?.message || 'Validation failed';
        message = msg;
      }
      if (!message) message = 'Failed to save maintenance. Please check your entries and try again.';
      showToast(message, 'error', 6000);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedMaintenance(null);
    setFormData({
      aircraftId: '',
      type: 'INSPECTION',
      title: '',
      description: '',
      priority: 'MEDIUM',
      status: 'SCHEDULED',
      scheduledDate: null,
      scheduleMode: 'FULL_DAY',
      scheduledEndDate: null,
      inspectionType: '',
      ampScheduleId: '',
      ampChecks: [],
      creationSourceRemarks: '',
      linkedIntimationIds: [],
      workOrderCsvFile: null,
      standaloneEmergencyRepair: false
    });
  };

  const handleEdit = (maintenanceItem, overrideJustification = '') => {
    if (overrideJustification) {
      setAmOverrideJustification(overrideJustification);
    }
    setSelectedMaintenance(maintenanceItem);
    const ampScheduleId = maintenanceItem.ampScheduleId
      ? (maintenanceItem.ampScheduleId._id || maintenanceItem.ampScheduleId).toString()
      : '';
    const linkedIds = Array.isArray(maintenanceItem.linkedIntimationIds)
      ? maintenanceItem.linkedIntimationIds.map((i) => i._id || i)
      : [];
    const mode = maintenanceItem.scheduleMode === 'HOURLY' ? 'HOURLY' : 'FULL_DAY';
    let scheduledDate = null;
    let scheduledEndDate = null;
    if (maintenanceItem.scheduledDate) {
      if (displayTimezone) {
        const { date: d, time: t } = utcToDisplayDateAndTime(maintenanceItem.scheduledDate, displayTimezone);
        scheduledDate = d
          ? dayjs.tz(`${d}T${mode === 'HOURLY' ? (t || '00:00') : '00:00'}`, displayTimezone)
          : dayjs(maintenanceItem.scheduledDate);
      } else {
        scheduledDate = dayjs(maintenanceItem.scheduledDate);
      }
    }
    if (mode === 'HOURLY' && maintenanceItem.scheduledEndDate) {
      if (displayTimezone) {
        const { date: d, time: t } = utcToDisplayDateAndTime(maintenanceItem.scheduledEndDate, displayTimezone);
        scheduledEndDate = d ? dayjs.tz(`${d}T${t || '00:00'}`, displayTimezone) : dayjs(maintenanceItem.scheduledEndDate);
      } else {
        scheduledEndDate = dayjs(maintenanceItem.scheduledEndDate);
      }
    }
    setFormData({
      ...maintenanceItem,
      creationSourceRemarks: maintenanceItem.creationSourceRemarks || '',
      scheduleMode: mode,
      scheduledDate,
      scheduledEndDate,
      ampScheduleId,
      linkedIntimationIds: linkedIds,
      workOrderCsvFile: null
    });
    setOpen(true);
  };

  const handleCancel = (wo) => {
    setSelectedMaintenance(wo);
    setCancelOpen(true);
  };

  const handleWorkflowAction = async (action, wo) => {
    try {
      const response = await axios.get(`/api/maintenance/${wo._id}`);
      setSelectedMaintenance(response.data);
    } catch (error) {
      console.error('Error fetching maintenance details:', error);
      setSelectedMaintenance(wo);
    }

    switch (action) {
      case 'camoQmAck':
      case 'camoQmCompletion':
        setDetailOpen(true);
        setDetailTab(WO_DETAIL_TABS.CAMO_QM_REVIEW);
        break;
      case 'planning':
        setPlanningOpen(true);
        break;
      case 'store':
        setStoreOpen(true);
        break;
      case 'externalAgency':
        setExternalAgencyOpen(true);
        break;
      case 'mro':
        setMroOpen(true);
        break;
      case 'mroEngineer':
        setMroEngineerOpen(true);
        break;
      case 'mroQmCompletion':
        setMroQmCompletionOpen(true);
        break;
      case 'partsProcurement':
        setPartsProcurementOpen(true);
        break;
      case 'releaseCertificate':
        setReleaseCertificateOpen(true);
        break;
      default:
        break;
    }
  };

  const handleWorkflowSuccess = () => {
    fetchMaintenance();
    if (detailOpen && selectedMaintenance) {
      // Refresh selected maintenance details
      axios.get(`/api/maintenance/${selectedMaintenance._id}`)
        .then(res => setSelectedMaintenance(res.data))
        .catch(err => console.error('Error fetching maintenance details:', err));
    }
  };

  const handleRefreshWorkOrder = (updatedWorkOrder) => {
    setSelectedMaintenance(updatedWorkOrder);
    fetchMaintenance();
  };

  const handleViewDetails = async (wo) => {
    try {
      const response = await axios.get(`/api/maintenance/${wo._id}`);
      setSelectedMaintenance(response.data);
      setDetailOpen(true);
      setDetailTab(WO_DETAIL_TABS.OVERVIEW);
    } catch (error) {
      console.error('Error fetching maintenance details:', error);
      showToast('Failed to fetch work order details', 'error');
    }
  };

  return {
    // Dialog states
    open,
    setOpen,
    detailOpen,
    setDetailOpen,
    detailTab,
    setDetailTab,
    selectedMaintenance,
    setSelectedMaintenance,
    formData,
    setFormData,
    
    // Workflow dialog states
    camoQmAckOpen,
    setCamoQmAckOpen,
    planningOpen,
    setPlanningOpen,
    storeOpen,
    setStoreOpen,
    externalAgencyOpen,
    setExternalAgencyOpen,
    mroOpen,
    setMroOpen,
    mroEngineerOpen,
    setMroEngineerOpen,
    mroQmCompletionOpen,
    setMroQmCompletionOpen,
    camoQmCompletionOpen,
    setCamoQmCompletionOpen,
    partsProcurementOpen,
    setPartsProcurementOpen,
    releaseCertificateOpen,
    setReleaseCertificateOpen,
    cancelOpen,
    setCancelOpen,
    amOverrideJustification,
    setAmOverrideJustification,
    
    // Handlers
    handleSubmit,
    handleClose,
    handleEdit,
    handleCancel,
    handleWorkflowAction,
    handleWorkflowSuccess,
    handleViewDetails,
    handleRefreshWorkOrder
  };
};
